import { generateUuid } from '@lib/uuid';
import { postPortalContentClientApi } from '@nx-frontend/shared/api';
import { getSlugFromUrl } from '@nx-frontend/shared/store/feature-menu-tree';
import { generateUrl } from '@nx-frontend/shared/store/virtual-portal-node';
import {
  AddFolderToPortalPayload,
  AllowAllPermissions,
  ContentBlock,
  MenuItemType,
  NoteMenuItem,
  NotesFolderMenuItem,
  PortalBlockType,
  PortalServiceChangeEventDto,
  PortalServiceNoteMenuItemDto,
  PortalServiceNotesFolderMenuItemDto,
} from '@nx-frontend/shared/types';
import { AddNoteToPortalPayload, GetPreparedPortalMenuItemPayload } from '@nx-frontend/shared/types';
import { getWrapperProps } from '@nx-frontend/shared/utils/blocks';

export const getPreparedPortalNoteMenuItem = (payload: GetPreparedPortalMenuItemPayload): NoteMenuItem => {
  const parentId = undefined === payload.parentId ? '' : payload.parentId;
  const positionType = undefined === payload.positionType ? 'sidebar' : payload.positionType;

  const pageId = generateUuid();
  const url = generateUrl(payload.pageName, payload.existedSlugs, payload.parentUrl);

  const noteMenuItem: NoteMenuItem = {
    type: MenuItemType.NOTE,
    hasChildren: false,
    icon: 'page',
    id: pageId,
    index: payload.index,
    name: payload.pageName,
    parentId,
    positionType,
    slug: getSlugFromUrl(payload.url ?? url),
    targetId: payload.noteId,
    url: payload.url ?? url,
    visible: payload.pageVisibility,
    openInNewTab: false,
    pageId: payload.pageId,
    permissions: AllowAllPermissions,
  };

  return noteMenuItem;
};

export const addNoteToPortal = async (payload: AddNoteToPortalPayload): Promise<boolean> => {
  const { name: pageName, index, url, visible: pageVisibility } = payload.menuItem;
  const path = undefined === payload.path ? '' : payload.path;
  const pageId = payload.pageId ?? generateUuid();
  const newBlock: ContentBlock = {
    id: generateUuid(),
    type: PortalBlockType.CONTENT,
    index: index,
    orgId: payload.orgId,
    workspaceId: payload.workspaceId,
    noteId: payload.noteId,
    place: 'page_body',
    colspan: 6,
    rowspan: 6,
    title: pageName,
    text: '',
    actions: [],
    wrapper: getWrapperProps('blank'),
    mediaSizes: ['base', 'md', 'lg'],
    enabled: true,
    removable: true,
    pageId,
    data: [],
  };
  try {
    await postPortalContentClientApi({
      changeEventsDto: [
        {
          type: 'addPage',
          newValue: {
            id: pageId,
            path: `${path}${url}`,
            title: pageName,
            visible: pageVisibility,
          },
          oldValue: undefined,
          meta: {
            userId: payload.userId,
            pageId: pageId,
          },
        },
        {
          type: 'addMenuItem',
          newValue: { ...payload.menuItem, pageId } as PortalServiceNoteMenuItemDto,
          oldValue: undefined,
          meta: {
            userId: payload.userId,
            pageId: pageId,
          },
        },
        {
          type: 'addBlock',
          newValue: newBlock,
          oldValue: undefined,
          meta: {
            userId: payload.userId,
            pageId: pageId,
          },
        },
      ],
      portalId: payload.portalId,
    });
    return true;
  } catch (err) {
    return false;
  }
};

export const getPreparedPortalFolderMenuItem = (payload: GetPreparedPortalMenuItemPayload): NotesFolderMenuItem => {
  const parentId = undefined === payload.parentId ? '' : payload.parentId;
  const positionType = undefined === payload.positionType ? 'sidebar' : payload.positionType;

  const pageId = generateUuid();
  const url = generateUrl(payload.pageName, payload.existedSlugs, payload.parentUrl);

  const folderMenuItem: NotesFolderMenuItem = {
    type: MenuItemType.NOTES_FOLDER,
    hasChildren: undefined === payload.hasChildren ? false : payload.hasChildren,
    icon: 'folder',
    id: pageId,
    index: payload.index,
    name: payload.pageName,
    parentId,
    positionType,
    slug: getSlugFromUrl(payload.url ?? url),
    targetId: payload.noteId,
    url: payload.url ?? url,
    visible: payload.pageVisibility,
    openInNewTab: false,
    permissions: AllowAllPermissions,
  };

  return folderMenuItem;
};

export const addFolderToPortal = async (payload: AddFolderToPortalPayload): Promise<boolean> => {
  const { name: pageName, url, visible: pageVisibility } = payload.menuItem;
  const path = undefined === payload.path ? '' : payload.path;
  const pageId = generateUuid();

  const addChildrenOps: PortalServiceChangeEventDto[] =
    undefined === payload.children
      ? []
      : payload.children.map((child) => {
          const childPageId = generateUuid();
          return {
            type: 'addPage',
            newValue: {
              id: childPageId,
              path: `${path}${child.url}`,
              title: child.name,
              visible: child.visible,
            },
            oldValue: undefined,
            meta: {
              pageId: childPageId,
              userId: payload.userId,
            },
          };
        });

  const addChildMenuItemsOps =
    undefined === payload.children
      ? []
      : payload.children.reduce((ops, child, index) => {
          const newChildBlock: ContentBlock | null =
            child.type === MenuItemType.NOTE
              ? {
                  id: generateUuid(),
                  type: PortalBlockType.CONTENT,
                  index: child.index,
                  orgId: payload.orgId,
                  workspaceId: payload.workspaceId,
                  noteId: child.targetId,
                  place: 'page_body',
                  colspan: 6,
                  rowspan: 6,
                  title: child.name,
                  text: '',
                  actions: [],
                  wrapper: getWrapperProps('blank'),
                  mediaSizes: ['base', 'md', 'lg'],
                  enabled: true,
                  removable: true,
                  pageId,
                  data: [],
                }
              : null;
          const childMenuItemDto =
            child.type === MenuItemType.NOTE
              ? (child as PortalServiceNoteMenuItemDto)
              : (child as PortalServiceNotesFolderMenuItemDto);
          ops.push({
            type: 'addMenuItem',
            newValue: { ...childMenuItemDto, pageId: addChildrenOps[index].meta.pageId },
            oldValue: undefined,
            meta: addChildrenOps[index].meta,
          });
          if (newChildBlock !== null) {
            ops.push({
              type: 'addBlock',
              newValue: newChildBlock,
              oldValue: undefined,
              meta: addChildrenOps[index].meta,
            });
          }
          return ops;
        }, [] as PortalServiceChangeEventDto[]);

  try {
    await postPortalContentClientApi({
      changeEventsDto: [
        {
          type: 'addPage',
          newValue: {
            id: pageId,
            path: `${path}${url}`,
            title: pageName,
            visible: pageVisibility,
          },
          oldValue: undefined,
          meta: {
            pageId: pageId,
            userId: payload.userId,
          },
        },
        ...addChildrenOps,
        {
          type: 'addMenuItem',
          newValue: { ...payload.menuItem, pageId } as PortalServiceNotesFolderMenuItemDto,
          oldValue: undefined,
          meta: {
            pageId: pageId,
            userId: payload.userId,
          },
        },
        ...addChildMenuItemsOps,
      ],
      portalId: payload.portalId,
    });
    return true;
  } catch (err) {
    console.log(err);
    return false;
  }
};
